import React, { memo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, Trash2 } from "lucide-react";
import { FONT_FAMILIES } from "@/lib/gameVisual";

export const AVAILABLE_VARIABLES = [
  { token: "{name}",               label: "Nome do aluno" },
  { token: "{school}",             label: "Escola" },
  { token: "{competition}",        label: "Competição" },
  { token: "{time}",               label: "Melhor tempo (s)" },
  { token: "{ranking}",            label: "Classificação" },
  { token: "{level}",              label: "Nível / Escalão" },
  { token: "{tier}",               label: "Nome do escalão" },
  { token: "{total_participants}", label: "Total participantes (nível)" },
  { token: "{avg_best_time}",      label: "Tempo médio (nível)" },
  { token: "{avg_time}",           label: "Tempo médio (jogo)" },
];

// values[lang] → values.pt → value (legacy canonical string).
export const fieldValueFor = (f, lang) => {
  const v = f?.values || {};
  if (v[lang] != null && v[lang] !== "") return v[lang];
  if (v.pt != null && v.pt !== "") return v.pt;
  return f?.value || "";
};

function InsertVariableMenu({ onInsert, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] font-bold text-sky-700 hover:text-sky-900 inline-flex items-center gap-0.5 bg-sky-50 hover:bg-sky-100 px-2 py-0.5 rounded"
      >
        + Inserir variável <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[220px] max-h-64 overflow-y-auto" data-testid={`${testid}-menu`}>
            {AVAILABLE_VARIABLES.map((v) => (
              <button
                key={v.token}
                data-testid={`${testid}-${v.token.replace(/[{}]/g, "")}`}
                onClick={() => { onInsert(v.token); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 hover:bg-sky-50 text-xs flex items-center justify-between gap-2"
              >
                <code className="text-sky-700 font-mono">{v.token}</code>
                <span className="text-slate-500 truncate">{v.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Memoised so typing in one field (or in the template name) does not re-render
// every other field block (each carries a Radix Select + Textarea).
export const DiplomaFieldEditor = memo(function DiplomaFieldEditor({ f, i, editLang, onPatch, onValue, onRemove }) {
  const current = fieldValueFor(f, editLang);

  const insertVariable = (token) => {
    const ta = document.getElementById(`tpl-field-value-${i}`);
    if (ta && typeof ta.selectionStart === "number") {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      onValue(i, editLang, current.slice(0, start) + token + current.slice(end));
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + token.length;
        ta.setSelectionRange(pos, pos);
      });
    } else {
      onValue(i, editLang, current + token);
    }
  };

  return (
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3">
          <Label className="text-[10px] uppercase text-slate-500">Nome</Label>
          <Input data-testid={`tpl-field-name-${i}`} value={f.name} onChange={(e) => onPatch(i, { name: e.target.value })} />
        </div>
        <div className="col-span-4">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase text-slate-500">Valor / Template</Label>
            <InsertVariableMenu testid={`tpl-field-insert-${i}`} onInsert={insertVariable} />
          </div>
          <Textarea
            data-testid={`tpl-field-value-${i}`}
            id={`tpl-field-value-${i}`}
            value={current}
            onChange={(e) => onValue(i, editLang, e.target.value)}
            placeholder="{name} — Enter para nova linha"
            rows={2}
            className="min-h-[40px] resize-y font-mono text-sm"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-slate-500">Cor</Label>
          <Input type="color" value={f.color} onChange={(e) => onPatch(i, { color: e.target.value })} />
        </div>
        <label className="col-span-1 flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={!!f.bold} onChange={(e) => onPatch(i, { bold: e.target.checked })} /> <b>B</b>
        </label>
        <label className="col-span-1 flex items-center gap-1 text-xs cursor-pointer">
          <input type="checkbox" checked={!!f.active} onChange={(e) => onPatch(i, { active: e.target.checked })} /> On
        </label>
        <button
          type="button"
          data-testid={`tpl-field-remove-${i}`}
          onClick={() => onRemove(i)}
          className="col-span-1 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded p-1"
          title="Remover este campo"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-12 gap-2 items-end mt-2">
        <div className="col-span-3">
          <Label className="text-[10px] uppercase text-slate-500">Fonte</Label>
          <Select value={f.font || "Helvetica"} onValueChange={(v) => onPatch(i, { font: v })}>
            <SelectTrigger data-testid={`tpl-field-font-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((fam) => (
                <SelectItem key={fam.value} value={fam.value}>{fam.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase text-slate-500">Tamanho (px)</Label>
          <Input data-testid={`tpl-field-size-${i}`} type="number" min={6} max={96} value={f.size} onChange={(e) => onPatch(i, { size: +e.target.value })} />
        </div>
        <div className="col-span-3">
          <Label className="text-[10px] uppercase text-slate-500">↕ Vertical <span className="opacity-60">(+ sobe, - desce)</span></Label>
          <Input data-testid={`tpl-field-nudgey-${i}`} type="number" min={-100} max={100} value={f.nudge_y ?? 0} onChange={(e) => onPatch(i, { nudge_y: Math.max(-100, Math.min(100, +e.target.value)) })} />
        </div>
        <div className="col-span-3">
          <Label className="text-[10px] uppercase text-slate-500">↔ Horizontal <span className="opacity-60">(+ direita, - esquerda)</span></Label>
          <Input data-testid={`tpl-field-nudgex-${i}`} type="number" min={-100} max={100} value={f.nudge_x ?? 0} onChange={(e) => onPatch(i, { nudge_x: Math.max(-100, Math.min(100, +e.target.value)) })} />
        </div>
        <button
          type="button"
          data-testid={`tpl-field-reset-${i}`}
          onClick={() => onPatch(i, { nudge_x: 0, nudge_y: 0 })}
          className="col-span-1 text-[10px] px-2 py-2 rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-50"
          title="Repor deslocamentos a 0"
        >
          Repor
        </button>
      </div>
    </div>
  );
});
